import { useCallback, useRef, useState } from "react";

import { isAPIError, readServiceError } from "@/core/services";
import { useToast } from "@/ds";
import { HUB_PERMISSOES, usePermissoes } from "@/modules/permissoes";
import type { EquipeObraTarget } from "@/modules/recursos";
import {
  MENSAGEM_ARQUIVAR,
  mensagemDescartar,
  persistirAlteracaoNaSimulacao,
  removerAlteracaoDaSimulacao,
  revalidarCenario,
  SIMULACAO_SAIR_LABEL,
  TITULO_SAIR_SIMULACAO,
  TOAST_DESFAZER_FALHOU,
  mensagemInstrucaoRecusada,
  TOAST_REVALIDACAO_FALHOU,
  TOAST_SIMULACAO_ARQUIVADA,
  TOAST_SIMULACAO_ARQUIVADA_DETALHE,
  TOAST_SIMULACAO_DESCARTADA,
  TOAST_SIMULACAO_DESCARTADA_DETALHE,
  useSimulacao,
} from "@/modules/recursos/simulacao";
import type {
  AlteracaoEquipe,
  AlteracaoSimulada,
  CargaAtualComparacao,
  CenarioSimulacao,
  DivergenciaAplicacao,
} from "@/modules/recursos/simulacao";
import {
  arquivarSimulacao,
  buscarSimulacaoAtiva,
  criarSimulacao,
  descartarSimulacao,
} from "@/modules/recursos/simulacao.gateway";

import { useCargaPeriodo } from "./hooks/use-carga-periodo";
import { DEFAULT_PERSPECTIVA, PERSPECTIVA_OPTIONS } from "./carga-de-trabalho.constants";
import type { CargaPerspectiva } from "./carga-de-trabalho.constants";
import type { EquipeObraSimulacao } from "./components/definir-equipe-obra";
import { useIncluirNaSimulacao } from "./hooks/use-incluir-na-simulacao";
import type {
  CargaAba,
  CargaDeTrabalhoViewModel,
  CargaNotice,
  DefinirEquipeSimulacao,
} from "./types-carga-de-trabalho";

/** Modais de gerenciar e aplicar o cenário. */
type GestaoSimulacao = "comparar" | "arquivar" | "aplicar" | "historico";

/** "Definir Equipe" (obra sem equipe) e "Editar Equipe" (com equipe) abrem o mesmo modal. */
interface EquipeObraAberta {
  readonly obra: EquipeObraTarget;
  readonly equipeDefinida: boolean;
  /** Presente só na simulação: o modal inclui a equipe no cenário em vez de gravar. */
  readonly simulacao?: DefinirEquipeSimulacao;
}

/**
 * Carga de Trabalho: perspectiva atual, Modo Simulação (entrar, aplicar, descartar, arquivar),
 * Papéis na Obra e Equipe da Obra. Cada perspectiva (aba) cuida do próprio período, busca e filtros.
 * Deve ficar dentro de `SimulacaoProvider` e `ToastProvider`.
 */
export function useCargaDeTrabalho(): CargaDeTrabalhoViewModel {
  const toast = useToast();
  // Fail-closed: enquanto carrega ou em erro, equipe e Modo Simulação ficam ocultos (o core barra com 403); a simulação inteira exige `apply`.
  const { hasPermissao } = usePermissoes();
  const podeGerirEquipe = hasPermissao(HUB_PERMISSOES.EQUIPES_CADASTRO);
  const podeSimular = hasPermissao(HUB_PERMISSOES.EQUIPE_APPLY);
  const simulacao = useSimulacao();
  const { cenario } = simulacao;
  const simulando = cenario !== null;

  const [perspectiva, setPerspectiva] = useState<CargaPerspectiva>(DEFAULT_PERSPECTIVA);
  // Na simulação, as perspectivas visitadas ficam montadas (escondidas) para manter
  // período, busca, agrupamento e filtros de cada uma ao voltar para ela.
  const [visitadas, setVisitadas] = useState<ReadonlySet<CargaPerspectiva>>(() => new Set());
  const periodoCompartilhado = useCargaPeriodo();
  const [papeisAberto, setPapeisAberto] = useState(false);
  const [equipeObra, setEquipeObra] = useState<EquipeObraAberta | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [gestao, setGestao] = useState<GestaoSimulacao | null>(null);
  const [cargaAtual, setCargaAtual] = useState<CargaAtualComparacao | null>(null);
  const registrarCargaAtual = useCallback((carga: CargaAtualComparacao | null) => {
    setCargaAtual(carga);
  }, []);
  // Potenciais problemas da revalidação feita ao clicar em Aplicar (RN020–RN023).
  const [divergencias, setDivergencias] = useState<DivergenciaAplicacao[]>([]);
  const verificando = useRef(false);
  // Muda quando a carga precisa ser refeita do zero (simulação aplicada): remonta as perspectivas.
  const [geracao, setGeracao] = useState(0);

  /** Toasts da tela: papéis, equipe, resolução de conflito e Modo Simulação. */
  const notificar = useCallback(
    ({ severity, summary, detail }: CargaNotice) => toast.show({ severity, summary, detail }),
    [toast],
  );

  // Toda inclusão no cenário passa por aqui: Toasts e o RN011 (justificativa dos conflitos novos).
  const { incluirNaSimulacao, justificativa } = useIncluirNaSimulacao(
    async (alteracao: AlteracaoSimulada) => {
      try {
        await persistirAlteracaoNaSimulacao({
          alteracao,
          incluir: simulacao.incluir,
          hidratar: simulacao.hidratarSimulacaoAtiva,
          registrarInstrucao: simulacao.registrarInstrucao,
        });
      } catch (error) {
        // A recusa aparece no modal que pediu a inclusão, não em Toast atrás dele.
        throw new Error(
          mensagemInstrucaoRecusada(isAPIError(error) ? readServiceError(error).message : ""),
        );
      }
    },
    notificar,
  );

  function escolherPerspectiva(proxima: CargaPerspectiva) {
    setPerspectiva(proxima);
    if (simulando) setVisitadas((atual) => new Set(atual).add(proxima));
  }

  /** Aplicar: antes de abrir o modal, reconsulta os dados atuais e aponta o que mudou. */
  async function abrirAplicar() {
    if (!cenario || verificando.current) return;
    verificando.current = true;
    try {
      setDivergencias(await revalidarCenario(cenario));
    } catch {
      // Sem a conferência, "nada mudou" seria um palpite: o modal não abre e a simulação segue.
      notificar({
        severity: "error",
        summary: TOAST_REVALIDACAO_FALHOU,
        detail: "Tente novamente.",
      });
      return;
    } finally {
      verificando.current = false;
    }
    setGestao("aplicar");
  }

  function encerrarSimulacao() {
    setGestao(null);
    simulacao.sair();
  }

  /** Descarte só sem alterações. Falha da API mantém o modo na tela. */
  async function confirmarDescarte() {
    if (simulacao.alteracoes > 0) {
      setGestao(null);
      return;
    }
    if (simulacao.simulacaoId !== null && simulacao.versao !== null) {
      try {
        await descartarSimulacao(simulacao.simulacaoId, simulacao.versao);
      } catch {
        notificar({ severity: "error", summary: "Não foi possível descartar a simulação." });
        return;
      }
    }
    simulacao.limparSimulacaoAtiva();
    encerrarSimulacao();
    notificar({
      severity: "success",
      summary: TOAST_SIMULACAO_DESCARTADA,
      detail: TOAST_SIMULACAO_DESCARTADA_DETALHE,
    });
  }

  /** Arquivo só com alterações. A simulação sai da tela e libera o rascunho ativo. */
  async function confirmarArquivo() {
    if (simulacao.alteracoes === 0) {
      setGestao(null);
      return;
    }
    if (simulacao.simulacaoId !== null && simulacao.versao !== null) {
      try {
        await arquivarSimulacao(simulacao.simulacaoId, simulacao.versao);
      } catch (error) {
        if (isAPIError(error) && readServiceError(error).code === "SIMULACAO_NAO_ATIVA") {
          simulacao.limparSimulacaoAtiva();
          encerrarSimulacao();
          notificar({
            severity: "warn",
            summary: "A simulação já foi encerrada.",
            detail: "O modo saiu da tela.",
          });
          return;
        }
        notificar({ severity: "error", summary: "Não foi possível arquivar a simulação." });
        return;
      }
    }
    simulacao.limparSimulacaoAtiva();
    encerrarSimulacao();
    notificar({
      severity: "success",
      summary: TOAST_SIMULACAO_ARQUIVADA,
      detail: TOAST_SIMULACAO_ARQUIVADA_DETALHE,
    });
  }

  /** "Ajustar na simulação": segue no modo só com o que não foi aplicado, sobre a carga atualizada. */
  function ajustarSimulacao(restantes: AlteracaoSimulada[]) {
    setGestao(null);
    simulacao.redefinir(restantes);
    setGeracao((atual) => atual + 1);
  }

  /** "Concluir": o aplicado fica gravado; o modo termina e a carga é refeita. */
  function concluirSimulacao() {
    simulacao.limparSimulacaoAtiva();
    encerrarSimulacao();
    setGeracao((atual) => atual + 1);
  }

  /** Desfazer: remove o item da API e do cenário local. Sem id na API, não apaga só na tela. */
  async function desfazer(chave: string) {
    const resultado = await removerAlteracaoDaSimulacao({
      chave,
      instrucaoId: simulacao.mapaChaveParaItemId.get(chave),
      remover: simulacao.remover,
      hidratar: simulacao.hidratarSimulacaoAtiva,
    });
    if (resultado === "falhou") {
      notificar({ severity: "error", summary: TOAST_DESFAZER_FALHOU });
    }
  }

  /** Entrada no Modo Simulação: reconsulta a ativa; se não houver rascunho, abre outro. */
  async function entrarSimulacao() {
    setVisitadas(new Set([perspectiva]));
    try {
      const existente = await buscarSimulacaoAtiva();
      if (existente?.status === "ATIVA") {
        simulacao.hidratarSimulacaoAtiva(existente);
        return;
      }
      simulacao.limparSimulacaoAtiva();
      const ativa = await criarSimulacao();
      simulacao.hidratarSimulacaoAtiva(ativa);
    } catch {
      notificar({ severity: "error", summary: "Não foi possível iniciar o Modo Simulação." });
    }
  }

  function editarEquipe(target: EquipeObraTarget, contexto?: DefinirEquipeSimulacao) {
    if (simulando && !contexto) return;
    setEquipeObra({ obra: target, equipeDefinida: true, simulacao: contexto });
  }

  function definirEquipe(target: EquipeObraTarget, contexto?: DefinirEquipeSimulacao) {
    // Na simulação o modal nunca grava: sem o contexto do cenário, não abre.
    if (simulando && !contexto) return;
    setEquipeObra({ obra: target, equipeDefinida: false, simulacao: contexto });
  }

  /**
   * O modal em simulação: a equipe confirmada entra no cenário. Com conflito novo (RN011),
   * antes pede a justificativa; voltar dela deixa o modal aberto, sem incluir.
   */
  function equipeNaSimulacao(
    atual: CenarioSimulacao,
    contexto: DefinirEquipeSimulacao,
  ): EquipeObraSimulacao {
    return {
      cenario: atual,
      obra: contexto.obra,
      anterior: contexto.anterior,
      onAplicar: async (alteracao: AlteracaoEquipe, ausencias) => {
        const resultado = await incluirNaSimulacao({
          alteracao,
          conflitosNovos: [...contexto.conflitosNovos(alteracao), ...ausencias],
        });
        if (resultado.ok) setEquipeObra(null);
        return resultado;
      },
    };
  }

  function montarAba(value: CargaPerspectiva): CargaAba {
    const base = {
      chave: `${value}-${geracao}`,
      oculta: value !== perspectiva,
      testId: `carga-de-trabalho-view-${value}`,
    };
    const comum = {
      cenario,
      periodoCompartilhado,
      onPerspectivaChange: escolherPerspectiva,
      onEntrarSimulacao: podeSimular ? () => void entrarSimulacao() : undefined,
      onAbrirHistorico: () => setGestao("historico"),
      onIncluirNaSimulacao: podeSimular ? incluirNaSimulacao : undefined,
      onCargaAtual: value === perspectiva ? registrarCargaAtual : undefined,
      onNotify: notificar,
    };

    if (value === "profissionais") {
      return {
        ...base,
        perspectiva: value,
        props: {
          ...comum,
          onOpenPapeis: () => setPapeisAberto(true),
          onDefinirEquipe: podeGerirEquipe ? definirEquipe : undefined,
          onEditarEquipe: podeGerirEquipe ? editarEquipe : undefined,
          reloadToken,
        },
      };
    }
    if (value === "fornecedor") {
      return {
        ...base,
        perspectiva: value,
        props: comum,
      };
    }
    return { ...base, perspectiva: value, props: comum };
  }

  const abas = PERSPECTIVA_OPTIONS.filter(
    ({ value }) => value === perspectiva || (simulando && visitadas.has(value)),
  ).map(({ value }) => montarAba(value));

  const fecharGestao = () => setGestao(null);

  return {
    abas,
    banner: simulando
      ? {
          alteracoes: simulacao.alteracoes,
          onAplicar: podeSimular ? () => void abrirAplicar() : undefined,
          onComparar: () => setGestao("comparar"),
          onDescartar:
            podeSimular && simulacao.alteracoes === 0 ? () => void confirmarDescarte() : undefined,
          onArquivar:
            podeSimular && simulacao.alteracoes > 0 ? () => setGestao("arquivar") : undefined,
          hasDivergencias: simulacao.divergenciasApi.length > 0,
        }
      : null,
    justificativa,
    comparar:
      cenario && gestao === "comparar"
        ? {
            cenario,
            cargaAtual,
            onClose: fecharGestao,
            onAplicar: podeSimular ? () => void abrirAplicar() : undefined,
          }
        : null,
    arquivar:
      gestao === "arquivar" && simulacao.alteracoes > 0
        ? {
            titulo: TITULO_SAIR_SIMULACAO,
            mensagem: `${mensagemDescartar(simulacao.alteracoes)} ${MENSAGEM_ARQUIVAR}`,
            confirmarLabel: SIMULACAO_SAIR_LABEL,
            onConfirmar: () => void confirmarArquivo(),
            onCancelar: fecharGestao,
            testId: "simulacao-arquivar-dialog",
          }
        : null,
    historico: gestao === "historico" ? { onClose: fecharGestao } : null,
    aplicar:
      cenario && gestao === "aplicar"
        ? {
            cenario,
            simulacaoId: simulacao.simulacaoId,
            versao: simulacao.versao,
            onAjustar: ajustarSimulacao,
            onConcluir: concluirSimulacao,
            onVoltar: fecharGestao,
            onDesfazer: (chave: string) => void desfazer(chave),
            onNotify: notificar,
            divergencias,
          }
        : null,
    papeis: papeisAberto ? { onHide: () => setPapeisAberto(false), onNotify: notificar } : null,
    equipe: equipeObra
      ? {
          obra: equipeObra.obra,
          equipeDefinida: equipeObra.equipeDefinida,
          onHide: () => setEquipeObra(null),
          onNotify: notificar,
          onSaved: () => {
            setEquipeObra(null);
            setReloadToken((token) => token + 1);
          },
          simulacao:
            cenario && equipeObra.simulacao
              ? equipeNaSimulacao(cenario, equipeObra.simulacao)
              : undefined,
        }
      : null,
  };
}
